<template>
  <div class="card gradient-line-chart">
    <div class="p-3 pb-0 card-header">
      <div class="chart-header-content">
        <div v-if="title || description">
          <h6 class="mt-3 ms-4" v-if="title">{{ title }}</h6>
          <!-- eslint-disable vue/no-v-html -->
          <p v-if="description" class="text-sm" v-html="description" />
        </div>
        <button
          v-if="showTypeToggle"
          type="button"
          class="btn btn-lg chart-type-toggle"
          :disabled="isLoading || !hasChartData"
          :aria-label="chartTypeToggleLabel"
          :title="chartTypeToggleLabel"
          @click="toggleChartType"
        >
          <i :class="chartTypeToggleIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
    <div class="p-3 card-body">
      <div
        v-if="isLoading"
        class="chart chart-skeleton"
        :style="{ height: chartHeight }"
        aria-hidden="true"
      >
        <div class="skeleton skeleton-line skeleton-line-lg"></div>
        <div class="skeleton skeleton-line skeleton-line-md"></div>
        <div class="skeleton skeleton-line skeleton-line-sm"></div>
      </div>
      <div v-else-if="hasChartData" class="chart" :style="{ height: chartHeight }">
        <canvas
          :id="id"
          :key="chartRenderKey"
          ref="canvas"
          class="chart-canvas"
        ></canvas>
      </div>
      <div
        v-else
        class="chart chart-empty"
        :style="{ height: chartHeight }"
      >
        <span class="text-sm text-muted">Sin datos</span>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "GradientLineChart",
  props: {
    id: {
      type: String,
      required: true,
    },
    height: {
      type: [String, Number],
      default: "300",
    },
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    chartColors: {
      type: Array,
      default: () => [],
    },
    showTypeToggle: {
      type: Boolean,
      default: true,
    },
    charType: {
      type: String,
      default: 'line',
    },
  },
  data() {
    return {
      activeChartType: this.charType === "bar" ? "bar" : "line",
      renderTimeout: null,
      renderAttempts: 0,
    };
  },

  mounted() {
    this.queueRenderChart();
  },
  beforeUnmount() {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }

    this.destroyChart();
  },
  watch: {
    loading() {
      this.$nextTick(() => {
        this.queueRenderChart();
      });
    },
    chart: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.queueRenderChart();
        });
      },
    },
    chartColors: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.queueRenderChart();
        });
      },
    },
    charType() {
      this.activeChartType = this.normalizedCharType;

      this.$nextTick(() => {
        this.queueRenderChart();
      });
    },
  },
  computed: {
    normalizedCharType() {
      return this.charType === "bar" ? "bar" : "line";
    },
    chartHeight() {
      if (typeof this.height === "number") {
        return `${this.height}px`;
      }

      return /^\d+$/.test(this.height) ? `${this.height}px` : this.height;
    },
    chartDatasets() {
      if (Array.isArray(this.chart.datasets)) {
        return this.chart.datasets;
      }

      return Object.values(this.chart.datasets || {});
    },
    resolvedChartColors() {
      const defaultColors = [
        this.getCssVariable("--bs-analiza-dark", "#202945"),
        this.getCssVariable("--bs-analiza-azul", "#236092"),
        this.getCssVariable("--bs-analiza-medium", "#6fb1c8"),
      ];

      return this.chartColors.length ? this.chartColors : defaultColors;
    },
    hasChartData() {
      return this.chartDatasets.some((dataset) => {
        return Array.isArray(dataset.data) && dataset.data.length;
      });
    },
    isLoading() {
      return this.loading;
    },
    chartRenderKey() {
      return `${this.id}-${this.activeChartType}-${this.chartDatasets.length}-${(this.chart.labels || []).length}-${this.resolvedChartColors.join("-")}`;
    },
    chartTypeToggleLabel() {
      return this.activeChartType === "line" ? "Ver barras" : "Ver líneas";
    },
    chartTypeToggleIcon() {
      return this.activeChartType === "line"
        ? "fas fa-chart-column"
        : "fas fa-chart-line";
    },
  },
  methods: {
    toggleChartType() {
      this.activeChartType = this.activeChartType === "line" ? "bar" : "line";

      this.$nextTick(() => {
        this.queueRenderChart();
      });
    },
    destroyChart() {
      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }
    },
    normalizeChartValue(value) {
      if (typeof value === "number") {
        return value;
      }

      if (value === null || value === undefined || value === "") {
        return null;
      }

      const normalizedValue = String(value)
        .replace(/\s/g, "")
        .replace("€", "")
        .replace(/\./g, "")
        .replace(",", ".");
      const numericValue = Number(normalizedValue);

      return Number.isNaN(numericValue) ? null : numericValue;
    },
    getCssVariable(name, fallback) {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim() || fallback;
    },
    hexToRgba(hex, alpha) {
      if (!hex || !hex.startsWith("#")) {
        return hex;
      }

      const normalizedHex = hex.replace("#", "");
      const bigint = parseInt(normalizedHex, 16);
      const red = (bigint >> 16) & 255;
      const green = (bigint >> 8) & 255;
      const blue = bigint & 255;

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    createDatasetGradient(ctx, color) {
      const gradient = ctx.createLinearGradient(0, 230, 0, 50);

      gradient.addColorStop(1, this.hexToRgba(color, 0.08));
      gradient.addColorStop(0.45, this.hexToRgba(color, 0.025));
      gradient.addColorStop(0, this.hexToRgba(color, 0));

      return gradient;
    },
    queueRenderChart() {
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }

      this.renderTimeout = setTimeout(() => {
        this.renderChart();
      }, 0);
    },
    renderChart() {
      if (this.isLoading) {
        this.destroyChart();
        return;
      }

      const datasets = this.chartDatasets;

      const canvas = this.$refs.canvas;

      if (!canvas) {
        return;
      }

      if (!canvas.clientWidth && this.renderAttempts < 5) {
        this.renderAttempts += 1;
        this.renderTimeout = setTimeout(() => {
          this.renderChart();
        }, 50);
        return;
      }

      this.renderAttempts = 0;

      var ctx = canvas.getContext("2d");

      const chartColors = this.resolvedChartColors;

      this.destroyChart();

      const isLineChart = this.activeChartType === "line";

      new Chart(ctx, {
        type: this.activeChartType,
        data: {
          labels: this.chart.labels,
          datasets: datasets.map((dataset, index) => ({
            label: dataset.label,
            tension: isLineChart ? 0.4 : 0,
            pointRadius: isLineChart ? 0 : undefined,
            borderColor: chartColors[index % chartColors.length],
            borderWidth: 2,
            backgroundColor: isLineChart
              ? this.createDatasetGradient(
                  ctx,
                  chartColors[index % chartColors.length]
                )
              : this.hexToRgba(chartColors[index % chartColors.length], 0.72),
            borderRadius: isLineChart ? 0 : 6,
            fill: isLineChart,
            data: (dataset.data || []).map(this.normalizeChartValue),
            maxBarThickness: isLineChart ? 6 : 34,
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "bottom",
              align: "center",
              labels: {
                boxWidth: 12,
                boxHeight: 12,
                color: "#333",
                padding: 18,
                usePointStyle: true,
                pointStyle: isLineChart ? "line" : "rectRounded",
                font: {
                  size: 13,
                  family: "Nunito Sans",
                  style: "normal",
                },
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y: {
              min: this.id === "chart-line" ? 0 : undefined,
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                padding: 10,
                color: "#333",
                font: {
                  size: 12,
                  family: "Nunito Sans",
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: false,
                drawOnChartArea: false,
                drawTicks: false,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: "#333",
                padding: 20,
                font: {
                  size: 12,
                  family: "Nunito Sans",
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
          },
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.gradient-line-chart {
  width: 100%;
  background-color: var(--bs-gray-200);
}

.chart-header-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  justify-content: space-between;
}

.chart-type-toggle {
  position: absolute;
  right: 10px;
  margin: 0;
  width: 40px;
  padding: 0;
  z-index: 99999;
  display: inline-flex;
  height: 2.35rem;
  align-items: center;
  justify-content: center;
  color: var(--bs-analiza-dark, #202945);
  border: 1px solid rgba(32, 41, 69, 0.18);
  background: rgba(255, 255, 255, 0.68);
  box-shadow: 0 0.25rem 0.75rem rgba(32, 41, 69, 0.08);
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: #fff;
    background: var(--bs-analiza-dark, #202945);
    border-color: var(--bs-analiza-dark, #202945);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.chart {
  position: relative;
  width: 100%;
  min-height: 220px;
}

.chart-empty,
.chart-skeleton {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-skeleton {
  flex-direction: column;
  gap: 1.25rem;
}

.chart-canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.skeleton {
  position: relative;
  overflow: hidden;
  background: #e9ecef;
  border-radius: 0.35rem;

  &::after {
    position: absolute;
    inset: 0;
    content: "";
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.75) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: skeleton-shimmer 1.25s infinite;
  }
}

.skeleton-line {
  height: 1rem;
}

.skeleton-line-lg {
  width: 76%;
}

.skeleton-line-md {
  width: 58%;
}

.skeleton-line-sm {
  width: 42%;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
